"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Users, Trophy, Clock, Heart } from "lucide-react";

export function TrustSection() {
  const t = useTranslations("Home.trust");
  const titleAnimation = useScrollAnimation({ threshold: 0.2 });
  const statsAnimation = useScrollAnimation({ threshold: 0.2 });
  const authorityAnimation = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-24 px-4 sm:px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/40 to-black"></div>
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Título Principal */}
        <div
          ref={titleAnimation.ref}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            titleAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <div className="space-y-2">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-[family-name:var(--font-big-shoulders)] uppercase leading-tight">
              Adrián & Anita
            </h2>
            <p className="text-xl sm:text-2xl lg:text-3xl text-gray-300 font-medium">
              {t("subtitle")}
            </p>
          </div>
        </div>

        {/* Cifras de Impacto */}
        <div
          ref={statsAnimation.ref}
          className={`mb-20 transition-all duration-1000 ease-out ${
            statsAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Alumnos Satisfechos */}
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-[family-name:var(--font-big-shoulders)]">
                {t("stats.students.number")}
              </div>
              <div className="text-sm sm:text-base text-gray-300 leading-tight">
                {t("stats.students.label")}
              </div>
            </div>

            {/* Campeones del Mundo */}
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-[family-name:var(--font-big-shoulders)]">
                {t("stats.championships.number")}
              </div>
              <div className="text-sm sm:text-base text-gray-300 leading-tight">
                {t("stats.championships.label")}
              </div>
            </div>

            {/* Años de Experiencia */}
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-[family-name:var(--font-big-shoulders)]">
                {t("stats.experience.number")}
              </div>
              <div className="text-sm sm:text-base text-gray-300 leading-tight">
                {t("stats.experience.label")}
              </div>
            </div>

            {/* Seguidores en Redes */}
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-[family-name:var(--font-big-shoulders)]">
                {t("stats.followers.number")}
              </div>
              <div className="text-sm sm:text-base text-gray-300 leading-tight">
                {t("stats.followers.label")}
              </div>
            </div>
          </div>
        </div>

        {/* Autoridad y Legado */}
        <div
          ref={authorityAnimation.ref}
          className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${
            authorityAnimation.isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          {/* Imagen */}
          <div className="relative">
            <div className="aspect-[4/5] relative overflow-hidden rounded-lg">
              <Image
                src="/images/con-vestuario.webp"
                alt={t("authority.imageAlt")}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Contenido */}
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight font-[family-name:var(--font-big-shoulders)] uppercase">
              {t("authority.subtitle")}
            </h3>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              {t("authority.description")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
